import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus, X, ShoppingBag, ShieldCheck, Truck, Trash2, Plus as PlusIcon, Sparkles } from 'lucide-react';
import { useCart, BULK_TIERS, getDiscountForQty } from './CartContext';
import { PRODUCTS } from '../products';
import { Vial } from '../Vial';
import { CTA } from '../CTA';
export function CartDrawer() {
  const {
    lines,
    isOpen,
    closeDrawer,
    setQty,
    removeItem,
    addItem,
    subtotal,
    totalSavings,
    totalCount
  } = useCart();
  // Lock body scroll when drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);
  // Close on escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeDrawer]);
  const hydrated = lines.map(l => {
    const p = PRODUCTS.find(x => x.id === l.productId);
    return p ? {
      ...l,
      product: p
    } : null;
  }).filter(Boolean) as Array<{
    productId: string;
    qty: number;
    product: (typeof PRODUCTS)[number];
  }>;
  // Upsells = products not in cart, prioritised by goal-match
  const cartGoals = new Set(hydrated.flatMap(h => h.product.goals));
  const upsells = PRODUCTS.filter(p => !lines.find(l => l.productId === p.id)).sort((a, b) => {
    const aMatch = a.goals.some(g => cartGoals.has(g)) ? 1 : 0;
    const bMatch = b.goals.some(g => cartGoals.has(g)) ? 1 : 0;
    if (bMatch !== aMatch) return bMatch - aMatch;
    // featured / bestsellers first
    const aBest = a.badge === 'Bestseller' ? 1 : 0;
    const bBest = b.badge === 'Bestseller' ? 1 : 0;
    return bBest - aBest;
  }).slice(0, 3);
  const nextTier = (() => {
    if (totalCount === 0) return null;
    const currentDiscount = getDiscountForQty(totalCount);
    return BULK_TIERS.find(t => t.discount > currentDiscount) ?? null;
  })();
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-37">
      {isOpen && <div className="fixed inset-0 z-50" data-id="element-38">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} onClick={closeDrawer} className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]" data-id="element-39" />

          {/* Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34,
        mass: 0.9
      }} className="absolute right-0 top-0 bottom-0 w-full sm:max-w-[460px] bg-background flex flex-col shadow-[0_24px_60px_-12px_rgba(15,20,16,0.30)]" role="dialog" aria-label="Shopping cart" data-id="element-40">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-border bg-card" data-id="element-41">
              <div className="flex items-center gap-2.5" data-id="element-42">
                <ShoppingBag className="h-4 w-4 text-foreground" strokeWidth={1.75} data-id="element-43" />
                <span className="text-[15px] font-semibold tracking-tight text-foreground" data-id="element-44">
                  Your cart
                </span>
                {totalCount > 0 && <span className="font-mono text-[11px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-45">
                    · {totalCount}
                  </span>}
              </div>
              <button onClick={closeDrawer} className="h-8 w-8 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Close cart" data-id="element-46">
                <X className="h-4 w-4" strokeWidth={1.75} data-id="element-47" />
              </button>
            </div>

            {/* Next-tier upsell banner */}
            {nextTier && <div className="px-6 py-3 border-b border-border bg-primary/5" data-id="element-48">
                <div className="flex items-center justify-between gap-3" data-id="element-49">
                  <span className="text-[12px] text-foreground leading-tight" data-id="element-50">
                    Add{' '}
                    <span className="font-mono text-foreground font-semibold" data-id="element-51">
                      {nextTier.qty - totalCount}
                    </span>{' '}
                    more {nextTier.qty - totalCount === 1 ? 'vial' : 'vials'} to{' '}
                    <span className="font-semibold text-primary" data-id="element-52">
                      {nextTier.savingsLabel?.toLowerCase()}
                    </span>
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold" data-id="element-53">
                    {nextTier.label}
                  </span>
                </div>
                <div className="mt-2 h-1 w-full rounded-full bg-border overflow-hidden" data-id="element-54">
                  <motion.div className="h-full bg-primary rounded-full" initial={false} animate={{
              width: `${Math.min(100, totalCount / nextTier.qty * 100)}%`
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-55" />
                </div>
              </div>}

            {/* Body */}
            <div className="flex-1 overflow-y-auto" data-id="element-56">
              {hydrated.length === 0 ? <EmptyState upsells={upsells} onAdd={id => addItem(id, 1)} onClose={closeDrawer} data-id="element-57" /> : <>
                  <ul className="divide-y divide-border" data-id="element-58">
                    {hydrated.map(({
                product,
                qty
              }) => {
                const d = getDiscountForQty(qty);
                const gross = product.price * qty;
                const net = gross * (1 - d);
                return <motion.li key={product.id} layout initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  x: 40
                }} transition={{
                  duration: 0.2
                }} className="px-6 py-4 flex gap-4" data-id="element-59">
                          <Link to={`/shop/${product.slug}`} onClick={closeDrawer} className="h-24 w-20 rounded-xl bg-secondary/50 border border-border flex items-center justify-center flex-shrink-0 text-foreground" data-id="element-60">
                            <Vial label={product.name} doseMg={product.doseMg} volumeMl={product.volumeMl} category={product.category} size={70} showShadow={false} showHighlight={false} data-id="element-61" />
                          </Link>
                          <div className="flex-1 min-w-0" data-id="element-62">
                            <div className="flex items-start justify-between gap-3" data-id="element-63">
                              <div className="min-w-0" data-id="element-64">
                                <Link to={`/shop/${product.slug}`} onClick={closeDrawer} className="text-[14px] font-semibold tracking-tight text-foreground hover:text-primary transition-colors block truncate" data-id="element-65">
                                  {product.name}
                                </Link>
                                <div className="font-mono text-[11px] text-muted-foreground mt-0.5 [font-feature-settings:'tnum']" data-id="element-66">
                                  <span className="text-foreground" data-id="element-67">
                                    {product.doseMg}
                                  </span>
                                  <span className="text-primary font-semibold" data-id="element-68">
                                    mg
                                  </span>
                                  {' · '}
                                  <span className="text-primary" data-id="element-69">
                                    {product.purity.toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                              <button onClick={() => removeItem(product.id)} aria-label={`Remove ${product.name}`} className="h-7 w-7 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex-shrink-0" data-id="element-70">
                                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-71" />
                              </button>
                            </div>

                            <div className="mt-3 flex items-center justify-between gap-3" data-id="element-72">
                              <div className="inline-flex items-center rounded-full border border-border bg-card overflow-hidden" data-id="element-73">
                                <button onClick={() => setQty(product.id, qty - 1)} className="h-8 w-8 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Decrease" data-id="element-74">
                                  <Minus className="h-3 w-3" strokeWidth={1.75} data-id="element-75" />
                                </button>
                                <span className="font-mono text-[13px] w-7 text-center text-foreground select-none [font-feature-settings:'tnum']" data-id="element-76">
                                  {qty}
                                </span>
                                <button onClick={() => setQty(product.id, qty + 1)} className="h-8 w-8 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Increase" data-id="element-77">
                                  <Plus className="h-3 w-3" strokeWidth={1.75} data-id="element-78" />
                                </button>
                              </div>
                              <div className="text-right" data-id="element-79">
                                {d > 0 && <div className="font-mono text-[10px] text-muted-foreground line-through leading-none [font-feature-settings:'tnum']" data-id="element-80">
                                    £{gross.toFixed(0)}
                                  </div>}
                                <div className={`font-mono text-[14px] font-medium leading-tight mt-0.5 [font-feature-settings:'tnum'] ${d > 0 ? 'text-primary' : 'text-foreground'}`} data-id="element-81">
                                  £{net.toFixed(0)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.li>;
              })}
                  </ul>

                  {/* Inline upsells */}
                  {upsells.length > 0 && <div className="px-6 py-6 border-t border-border bg-secondary/30" data-id="element-82">
                      <div className="flex items-center gap-2 mb-3" data-id="element-83">
                        <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} data-id="element-84" />
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-85">
                          Often added with your cart
                        </span>
                      </div>
                      <ul className="space-y-2" data-id="element-86">
                        {upsells.map(p => <li key={p.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-card border border-border" data-id="element-87">
                            <div className="h-10 w-9 rounded-md bg-secondary/50 flex items-center justify-center flex-shrink-0 text-foreground" data-id="element-88">
                              <Vial label="" doseMg={p.doseMg} volumeMl={p.volumeMl} size={32} showShadow={false} showHighlight={false} data-id="element-89" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-90">
                              <div className="text-[13px] font-semibold tracking-tight text-foreground truncate" data-id="element-91">
                                {p.name}
                              </div>
                              <div className="font-mono text-[10.5px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-92">
                                £{p.price} ·{' '}
                                <span className="text-primary" data-id="element-93">
                                  {p.purity.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            <button onClick={() => addItem(p.id, 1)} className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-foreground/[0.04] hover:bg-primary hover:text-primary-foreground text-foreground text-[12px] font-medium transition-colors border border-border hover:border-primary" data-id="element-94">
                              <PlusIcon className="h-3 w-3" strokeWidth={2} data-id="element-95" />
                              Add
                            </button>
                          </li>)}
                      </ul>
                    </div>}
                </>}
            </div>

            {/* Footer */}
            {hydrated.length > 0 && <div className="border-t border-border bg-card" data-id="element-96">
                {totalSavings > 0 && <div className="px-6 pt-4 flex items-center justify-between text-[12px]" data-id="element-97">
                    <span className="text-muted-foreground" data-id="element-98">Bulk savings</span>
                    <span className="font-mono text-primary font-medium [font-feature-settings:'tnum']" data-id="element-99">
                      −£{totalSavings.toFixed(0)}
                    </span>
                  </div>}
                <div className="px-6 pt-3 flex items-center justify-between" data-id="element-100">
                  <span className="text-[13px] text-muted-foreground" data-id="element-101">
                    Subtotal
                  </span>
                  <span className="font-mono text-[22px] font-medium text-foreground tracking-[-0.01em] [font-feature-settings:'tnum']" data-id="element-102">
                    £{subtotal.toFixed(0)}
                  </span>
                </div>
                <p className="px-6 mt-1 text-[11px] text-muted-foreground" data-id="element-103">
                  Free UK delivery on orders over{' '}
                  <span className="font-mono text-foreground" data-id="element-104">£75</span>. Taxes
                  at checkout.
                </p>
                <div className="px-6 pt-4 pb-3 flex flex-col gap-2" data-id="element-105">
                  <CTA variant="primary" size="lg" fullWidth onClick={closeDrawer} to="/cart" data-id="element-106">
                    Checkout
                  </CTA>
                  <Link to="/cart" onClick={closeDrawer} className="text-center text-[12px] text-muted-foreground hover:text-foreground transition-colors py-1" data-id="element-107">
                    View full cart
                  </Link>
                </div>
                <div className="px-6 pb-5 flex items-center justify-center gap-5 text-[10.5px] text-muted-foreground" data-id="element-108">
                  <span className="inline-flex items-center gap-1.5" data-id="element-109">
                    <ShieldCheck className="h-3 w-3 text-primary" strokeWidth={1.75} data-id="element-110" />
                    30-day guarantee
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-111">
                    <Truck className="h-3 w-3 text-primary" strokeWidth={1.75} data-id="element-112" />
                    Same-day UK dispatch
                  </span>
                </div>
              </div>}
          </motion.div>
        </div>}
    </AnimatePresence>, document.body);
}
function EmptyState({
  upsells,
  onAdd,
  onClose
}: {
  upsells: (typeof PRODUCTS)[number][];
  onAdd: (id: string) => void;
  onClose: () => void;
}) {
  return <div className="flex flex-col h-full" data-id="element-113">
      {/* Hero empty card */}
      <div className="px-6 pt-10 pb-8 text-center" data-id="element-114">
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="inline-flex flex-col items-center" data-id="element-115">
          <div className="h-20 w-16 rounded-xl bg-secondary/60 border border-border flex items-center justify-center mb-5 text-foreground" data-id="element-116">
            <Vial label="" size={56} showShadow={false} showHighlight={false} data-id="element-117" />
          </div>
          <h3 className="text-[18px] font-semibold tracking-tight text-foreground" data-id="element-118">
            Your cart is empty
          </h3>
          <p className="text-[13px] text-muted-foreground mt-2 max-w-[280px] leading-relaxed" data-id="element-119">
            Add your first independently HPLC-verified peptide. Same-day UK
            dispatch on every order.
          </p>
          <div className="mt-5" data-id="element-120">
            <CTA to="/shop" variant="primary" size="md" onClick={onClose} data-id="element-121">
              Browse the catalogue
            </CTA>
          </div>
        </motion.div>
      </div>

      {/* Upsells: bestsellers to seed the cart */}
      {upsells.length > 0 && <div className="px-6 pb-8 mt-2" data-id="element-122">
          <div className="flex items-center gap-2 mb-3" data-id="element-123">
            <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} data-id="element-124" />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-125">
              Researchers usually start here
            </span>
          </div>
          <ul className="space-y-2" data-id="element-126">
            {upsells.map(p => <li key={p.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-card border border-border" data-id="element-127">
                <div className="h-12 w-10 rounded-md bg-secondary/50 flex items-center justify-center flex-shrink-0 text-foreground" data-id="element-128">
                  <Vial label="" doseMg={p.doseMg} volumeMl={p.volumeMl} size={36} showShadow={false} showHighlight={false} data-id="element-129" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-130">
                  <div className="text-[13.5px] font-semibold tracking-tight text-foreground truncate" data-id="element-131">
                    {p.name}
                  </div>
                  <div className="font-mono text-[10.5px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-132">
                    £{p.price} ·{' '}
                    <span className="text-primary" data-id="element-133">{p.purity.toFixed(1)}%</span>{' '}
                    HPLC
                  </div>
                </div>
                <button onClick={() => onAdd(p.id)} className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-primary text-primary-foreground text-[12px] font-medium hover:-translate-y-px transition-transform [box-shadow:0_1px_0_0_rgba(255,255,255,0.18)_inset,0_2px_6px_-2px_rgba(20,60,30,0.30)]" data-id="element-134">
                  <PlusIcon className="h-3 w-3" strokeWidth={2} data-id="element-135" />
                  Add
                </button>
              </li>)}
          </ul>
        </div>}
    </div>;
}